import {Component, ElementRef, HostListener, inject, input, output} from '@angular/core';
import {ImagesService} from '../../../../services/general/images.service';
import {Router} from '@angular/router';
import {Institution} from '../../../../interfaces/responses/institution.interface';
import {DatePipe} from '@angular/common';
import {InstitutionService} from '../../../../services/institution/institution.service';
import {ToastService} from '../../../../services/general/toast.service';
import {ConfirmationDialogComponent} from '../../../shared/confirmation-dialog/confirmation-dialog.component';
import {PermissionService} from '../../../../services/permission/permission.service';

@Component({
  selector: 'app-institution-tabel',
  imports: [
    DatePipe,
    ConfirmationDialogComponent
  ],
  templateUrl: './institution-tabel.component.html',
  styleUrl: './institution-tabel.component.scss'
})
export class InstitutionTabelComponent {
  _images = inject(ImagesService);
  isActionClick: boolean = false;
  _router = inject(Router)
  _eref = inject(ElementRef)
  isAddMember: boolean = false;
  institutionService = inject(InstitutionService);
  permissionService = inject(PermissionService);
  activeMenuItem: string = '';
  _toast = inject(ToastService);
  isUpdateInstitution = input<boolean>(true);
  showProfileIcon = input<boolean>(true);
  showDeleteDialog: boolean = false;
  showStatusDialog: boolean = false;

  institution = input<Institution>();

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this._eref.nativeElement.contains(event.target)) {
      this.isActionClick = false;
    }
  }

  addMembersEvent = output<boolean>();
  onAddMembers() {
    const institution = this.institution();
    if (!institution) {
      console.error('Institution is undefined. Cannot update institution.');
      return;
    }
    this.updateInstitutionEvent.emit(institution);
    this.addMembersEvent.emit(true);
  }

  get dropdownOptions(): any[] {
    const institution = this.institution();
    const isActive = institution?.isActive ?? false;
    const canManage = this.permissionService.canManage('institutions');

    const options = [
      {
        title: 'Add members',
        icon: this._images.addIcon
      },
      {
        title: 'View',
        icon: this._images.arrowForward
      },
      {
        title: 'Edit',
        icon: this._images.editIcon
      },
      {
        title: isActive ? 'Deactivate' : 'Activate',
        icon: isActive ? this._images.deniedIcon : this._images.checkIcon
      },
      {
        title: 'Delete',
        icon: this._images.deleteIcon
      },
    ];

    return canManage ? options : options.filter(option => option.title === 'View');
  }

  onActionClick() {
    this.isActionClick = !this.isActionClick;
  }

  showDeleteConfirmation() {
    this.showDeleteDialog = true;
  }

  cancelDelete() {
    this.showDeleteDialog = false;
  }

  confirmDelete() {
    const id = this.institution()?.id;
    if (!id) {
      console.error('Institution ID is undefined. Cannot delete institution.');
      return;
    }

    this.institutionService.deleteInstitution(id).subscribe({
      next: (response) => {
        this._toast.success('Institution deleted successfully');
        this.showDeleteDialog = false;
        this.loadInstitutionData();
      },
      error: (error) => {
        console.error('Error deleting institution:', error);
        this._toast.error('Error deleting institution');
        this.showDeleteDialog = false;
      }
    });
  }

  loadInstitutionEvent = output<void>()
  loadInstitutionData() {
    this.loadInstitutionEvent.emit();
  }

  updateInstitutionEvent = output<Institution>()
  showOverlayEvent = output<void>()

  updateInstitution() {
    const institution = this.institution();
    if (!institution) {
      console.error('Institution is undefined. Cannot update institution.');
      return;
    }
    this.updateInstitutionEvent.emit(institution);
    this.showOverlayEvent.emit();
  }

  onOptionClick(id: string) {
    switch(id) {
      case 'View':
        this._router.navigate(
          ['/institution-detail', this.institution()?.id],
          { state: { institution: this.institution() } }
        );
        break;
      case 'Add members':
        this.onAddMembers();
        this.isActionClick = false;
        break;
      case 'Edit':
        this.updateInstitution();
        this.isActionClick = false;
        break;
      case 'Activate':
      case 'Deactivate':
        this.showStatusConfirmation();
        this.isActionClick = false;
        break;
      case 'Delete':
        this.showDeleteConfirmation();
        this.isActionClick = false;
        break;
      default:
        return;
    }
  }

  showStatusConfirmation() {
    this.showStatusDialog = true;
  }

  cancelStatusToggle() {
    this.showStatusDialog = false;
  }

  confirmStatusToggle() {
    const id = this.institution()?.id;
    if (!id) {
      console.error('Institution ID is undefined. Cannot toggle institution status.');
      this.showStatusDialog = false;
      return;
    }

    const institution = this.institution();
    const currentStatus = institution?.isActive ?? false;
    const action = currentStatus ? 'deactivated' : 'activated';

    this.institutionService.toggleStatus(id).subscribe({
      next: (response) => {
        this._toast.success(`Institution ${action} successfully`);
        this.showStatusDialog = false;
        this.loadInstitutionData();
      },
      error: (error) => {
        console.error('Error toggling institution status:', error);
        this._toast.error(`Error ${action} institution`);
        this.showStatusDialog = false;
      }
    });
  }
}
